const JSVBAInterface = {
    // Collections
    allRequests: [],
    pendingRequests: [],
    completedRequests: [],

    // Processing lock
    isProcessing: false,

    // Stored column indices
    columnIndices: {
        jsID: null,
        jsRequest: null,
        jsParameters: null,
        vbaResponse: null,
        vbaStatus: null
    },

    // Minimum version required
    MIN_VERSION: 2,

    // Initialization flag
    isInitialized: false,

    // Health check startup method
    healthCheckStartup: async function () {
        try {
            await Excel.run(async (context) => {
                const sheet = context.workbook.worksheets.getItemOrNullObject("JSVBA Interface");
                await context.sync();
                if (sheet.isNullObject) {
                    throw new Error("Worksheet 'JSVBA Interface' does not exist.");
                }

                const requiredNames = ["jsID", "jsRequest", "jsParameters", "vbaResponse", "vbaStatus", "jsNewIDs", "vbaRequestStatus"];
                for (const name of requiredNames) {
                    const range = sheet.names.getItemOrNullObject(name);
                    await context.sync();
                    if (range.isNullObject) {
                        throw new Error(`Named range '${name}' is missing.`);
                    }
                }

                sheet.load("standardWidth");
                await context.sync();
                const version = sheet.standardWidth;
                if (version < this.MIN_VERSION) {
                    throw new Error(`Version ${version} is below the minimum required version ${this.MIN_VERSION}.`);
                }

                for (const name of ["jsID", "jsRequest", "jsParameters", "vbaResponse", "vbaStatus"]) {
                    const range = sheet.names.getItem(name).getRange();
                    range.load("columnIndex");
                    await context.sync();
                    this.columnIndices[name] = range.columnIndex;
                }

                this.isInitialized = true;
                console.log("Health check passed. Column indices:", this.columnIndices);
            });
        } catch (error) {
            console.error("Health check failed:", error.message);
            this.isInitialized = false;
            throw error;
        }
    },

    // Class to represent a request from JS to VBA
    JSRequestToVBA: class {
        constructor(id, request, parameters) {
            this.id = id;
            this.request = request;
            this.parameters = parameters;
            this.response = null;
            this.status = null;
        }
    },

    // Function to insert a new request into the worksheet
    insertNewRequest: async function (request, parameters) {
        if (!this.isInitialized) {
            throw new Error("Health check not performed. Run healthCheckStartup() first.");
        }

        return Excel.run(async (context) => {
            const sheet = context.workbook.worksheets.getItem("JSVBA Interface");
            const idCol = this.columnIndices.jsID;

            const idCell = sheet.getCell(1, idCol);
            idCell.load("values");
            await context.sync();
            const currentId = idCell.values[0][0];
            const maxId = (typeof currentId === 'number') ? currentId : 0;
            const newId = maxId + 1;

            const newRow = sheet.getRange("2:2");
            newRow.insert(Excel.InsertShiftDirection.down);

            const rowBelow = sheet.getRange("3:3");
            newRow.copyFrom(rowBelow, Excel.RangeCopyType.formats); //changee

            sheet.getCell(1, idCol).values = [[newId]];
            sheet.getCell(1, this.columnIndices.jsRequest).values = [[request]];
            sheet.getCell(1, this.columnIndices.jsParameters).values = [[parameters]];

            await context.sync();

            const newRequest = new this.JSRequestToVBA(newId, request, parameters);
            this.allRequests.push(newRequest);
            this.pendingRequests.push(newRequest);

            return newId;
        });
    },

    // Function to submit new request IDs to VBA via the jsNewIDs named range
    submitNewIDs: async function () {
        if (!this.isInitialized) {
            throw new Error("Health check not performed. Run healthCheckStartup() first.");
        }

        return Excel.run(async (context) => {
            const sheet = context.workbook.worksheets.getItem("JSVBA Interface");

            const statusRange = sheet.names.getItem("vbaRequestStatus").getRange();
            statusRange.values = [[""]];

            const jsNewIDsRange = sheet.names.getItem("jsNewIDs").getRange();

            const newIds = this.allRequests.map(req => req.id);
            const idString = newIds.join(",");

            jsNewIDsRange.values = [[idString]];

            await context.sync();
        });
    },

    // Function to await responses from VBA using binding event with status handling
    awaitResponses: async function () {
        if (!this.isInitialized) {
            throw new Error("Health check not performed. Run healthCheckStartup() first.");
        }

        const TIMEOUT_MS = 5000; // 5 seconds timeout

        return new Promise((resolve, reject) => {
            let timeoutId;

            Excel.run(async (context) => {
                const sheet = context.workbook.worksheets.getItem("JSVBA Interface");
                const statusRange = sheet.names.getItem("vbaRequestStatus").getRange();
                const binding = context.workbook.bindings.add(statusRange, Excel.BindingType.range, "statusBinding");

                context.trackedObjects.add(binding);

                binding.onDataChanged.add(async () => {
                    try {
                        await Excel.run(binding, async (newContext) => {
                            const currentRange = binding.getRange();
                            currentRange.load("values");
                            await newContext.sync();

                            const status = currentRange.values[0][0];
                            if (status === "Complete") {
                                const completedRequests = await this.fetchResponses(newContext);
                                binding.delete();
                                context.trackedObjects.remove(binding);
                                clearTimeout(timeoutId);
                                resolve(completedRequests);
                            } else if (status === "Error") {
                                const errorRequests = await this.fetchResponses(newContext);
                                binding.delete();
                                context.trackedObjects.remove(binding);
                                clearTimeout(timeoutId);
                                reject(new Error("VBA processing failed. Check request details for errors."));
                            } else if (status === "Processing" || !status) {
                                // Continue waiting
                            } else {
                                binding.delete();
                                context.trackedObjects.remove(binding);
                                clearTimeout(timeoutId);
                                reject(new Error("Unexpected status: " + status));
                            }
                        });
                    } catch (error) {
                        context.trackedObjects.remove(binding);
                        clearTimeout(timeoutId);
                        reject(error);
                    }
                });

                timeoutId = setTimeout(async () => {
                    try {
                        await Excel.run(async (timeoutContext) => {
                            const timeoutBinding = timeoutContext.workbook.bindings.getItem("statusBinding");
                            timeoutBinding.delete();
                            context.trackedObjects.remove(binding);
                            reject(new Error("Timeout: VBA did not respond within 5 seconds."));
                        });
                    } catch (error) {
                        reject(error);
                    }
                }, TIMEOUT_MS);

                await context.sync();
            }).catch((error) => {
                reject(error);
            });
        });
    },

    // Helper function to fetch responses from the worksheet
    fetchResponses: async function (context) {
        const sheet = context.workbook.worksheets.getItem("JSVBA Interface");
        const idCol = this.columnIndices.jsID;
        const statusCol = this.columnIndices.vbaStatus;
        const responseCol = this.columnIndices.vbaResponse;

        const maxRows = 100;
        const idRange = sheet.getRangeByIndexes(1, idCol, maxRows, 1);
        const statusRange = sheet.getRangeByIndexes(1, statusCol, maxRows, 1);
        const responseRange = sheet.getRangeByIndexes(1, responseCol, maxRows, 1);
        idRange.load("values");
        statusRange.load("values");
        responseRange.load("values");
        await context.sync();

        const idValues = idRange.values;
        const statusValues = statusRange.values;
        const responseValues = responseRange.values;

        const requests = [];
        for (const request of this.allRequests) {
            for (let i = 0; i < idValues.length; i++) {
                if (idValues[i][0] == request.id) {
                    request.status = statusValues[i][0];
                    request.response = responseValues[i][0];
                    requests.push(request);
                    break;
                }
            }
        }
        return requests;
    },

    // Utility function to clear all collections
    reset: function () {
        this.allRequests = [];
        this.pendingRequests = [];
        this.completedRequests = [];
    }
};


async function submitAndAwaitRequests(requestsData) {
    if (JSVBAInterface.isProcessing) {
        throw new Error("Another batch is being processed. Please wait.");
    }
    JSVBAInterface.isProcessing = true;
    try {
        await JSVBAInterface.healthCheckStartup();

        JSVBAInterface.reset(); // Clear collections at the start

        for (const data of requestsData) {
            const { request, parameters } = data;
            await JSVBAInterface.insertNewRequest(request, parameters);
        }

        await JSVBAInterface.submitNewIDs();

        const completedRequests = await JSVBAInterface.awaitResponses();
        console.log("All requests completed:", completedRequests);
        return completedRequests;

        JSVBAInterface.reset(); 
    } catch (error) {
        console.error("Error in request processing:", error);
        throw error;
    } finally {
        JSVBAInterface.isProcessing = false;
    }
}


window.submitAndAwaitRequests = submitAndAwaitRequests;
// window.requestsData = requestsData;